var _dec, _class, _DrawerContent;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { debounce } from '@instructure/debounce';
import { getBoundingClientRect } from '@instructure/ui-dom-utils';
import { omitProps } from '@instructure/ui-react-utils';
import { withStyle } from '@instructure/emotion';
import generateStyle from "./styles.js";
import generateComponentTheme from "./theme.js";
import { allowedProps } from "./props.js";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
/**
---
parent: DrawerLayout
id: DrawerLayout.Content
---
**/
let DrawerContent = (_dec = withStyle(generateStyle, generateComponentTheme), _dec(_class = (_DrawerContent = class DrawerContent extends Component {
  constructor(...args) {
    super(...args);
    this.ref = null;
    this._resizeListener = void 0;
    this._debounced = void 0;
    this.makeStyleProps = shouldTransition => {
      return {
        shouldTransition
      };
    };
    this.handleContentRef = node => {
      this.ref = node;
      if (typeof this.props.contentRef === 'function') {
        this.props.contentRef(node);
      }
    };
  }
  get _content() {
    return this.ref;
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    const rect = getBoundingClientRect(this.ref);
    const origSize = {
      width: rect.width
    };
    // set initial size
    if (typeof this.props.onSizeChange === 'function') {
      this.props.onSizeChange({
        width: rect.width,
        height: rect.height
      });

      // listen for changes to size
      this._debounced = debounce(this.props.onSizeChange, 100, {
        leading: false,
        trailing: true
      });
    }
    this._resizeListener = new ResizeObserver(entries => {
      for (const entry of entries) {
        const size = {
          width: entry.contentRect.width
        };
        if (size.width !== origSize.width) {
          var _this$_debounced;
          (_this$_debounced = this._debounced) === null || _this$_debounced === void 0 ? void 0 : _this$_debounced.call(this, size);
        }
      }
    });
    this._resizeListener.observe(this.ref);
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props, this.makeStyleProps(false));
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2, this.makeStyleProps(true));
  }
  componentWillUnmount() {
    var _this$_resizeListener, _this$_debounced2;
    (_this$_resizeListener = this._resizeListener) === null || _this$_resizeListener === void 0 ? void 0 : _this$_resizeListener.disconnect();
    (_this$_debounced2 = this._debounced) === null || _this$_debounced2 === void 0 ? void 0 : _this$_debounced2.cancel();
  }
  render() {
    var _this$props$styles;
    const _this$props3 = this.props,
      style = _this$props3.style,
      label = _this$props3.label,
      role = _this$props3.role;
    return _jsx("div", {
      ...omitProps(this.props, DrawerContent.allowedProps),
      role: role,
      style: style,
      ref: this.handleContentRef,
      "aria-label": label,
      css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.drawerContent,
      "data-cid": "DrawerContent",
      children: this.props.children
    });
  }
}, _DrawerContent.displayName = "DrawerContent", _DrawerContent.componentId = 'DrawerLayout.Content', _DrawerContent.locatorAttribute = 'data-drawer-content', _DrawerContent.allowedProps = allowedProps, _DrawerContent.defaultProps = {
  role: 'region'
}, _DrawerContent)) || _class);
export default DrawerContent;
export { DrawerContent };